import {Component, computed, effect, EventEmitter, inject, model, ModelSignal, Signal} from '@angular/core';
import {
  CSV_EXPORT_ACTION,
  DialogService,
  ITableAction,
  SharedGridOptions,
  TableControlComponent,
  TableControlModule,
  ToolbarStore,
} from "frontier/browserkit";
import {EControlActions, EsvgFiles, IControlDataChanged, IControlObject, INITIAL_STATE_TOKEN} from "frontier/nucleus";
import {
  BehaviorSubject,
  combineLatest,
  concatMap,
  debounceTime,
  distinctUntilChanged,
  forkJoin,
  merge,
  Observable,
  of,
  scan,
  startWith,
  Subscription,
  switchMap,
  tap,
} from "rxjs";
import {GridOptions} from "ag-grid-community";
import {catchError, filter, finalize, map} from 'rxjs/operators';
import {ECalculusStateFilter} from '../../../config/state-filter.enum';
import {ProcessOverviewStore} from '../service/process-overview.store';
import {takeUntilDestroyed, toObservable} from '@angular/core/rxjs-interop';
import {ControlStore} from 'frontier/nucleus/src/lib/apiv2/control.store';
import {IPdfBlob} from 'frontier/browserkit/src/lib/components/control/table-control/pdf-blob-event.interface';
import AppControlGuids from '../../../../api/ControlGuids';
import {IInvoice, IPdfOverlay, IProcessState} from '../service/state.interface';
import {OutboxBookService} from "../../../../api/generated";
import {
  DispatchTypeSelectorComponent,
  EJobType, IDispatchTypeSelectorDialogData, IDispatchTypeSelectorDialogReturnData,
} from "./dispatch-type-selector/dispatch-type-selector.component";

@Component({
  selector: 'kpi4me-document-outbox',
  templateUrl: '../../../../../../projects/frontier/browserkit/src/lib/components/control/table-control/table-control.component.html',
  styleUrl: '../../../../../../projects/frontier/browserkit/src/lib/components/control/table-control/table-control.component.scss',
  standalone: true,
  imports: [TableControlModule],
  providers: [
    {provide: INITIAL_STATE_TOKEN, useValue: {filter: null}},
    {provide: TableControlComponent, useExisting: DocumentOutboxComponent},
  ],
})
export class DocumentOutboxComponent extends TableControlComponent {
  private _toolbarStore: ToolbarStore = inject(ToolbarStore);
  private _processOverviewService: ProcessOverviewStore = inject(ProcessOverviewStore);
  private _dialogService: DialogService = inject(DialogService);
  private _outboxBookService: OutboxBookService = inject(OutboxBookService);
  private _controlStore: ControlStore = inject(ControlStore);
  private selectedProcess$: Observable<IProcessState> = toObservable(this._processOverviewService.selectedProcess);
  private selectedInvoice: Signal<IInvoice> = this._processOverviewService.selectedInvoice;
  private selectedInvoice$: Observable<IInvoice> = toObservable(this.selectedInvoice);
  private selectedDocumentOutbox: Signal<IControlObject> = this._processOverviewService.selectedDocumentOutbox;
  private selectedDocumentOutbox$: Observable<IControlObject> = toObservable(this.selectedDocumentOutbox);
  private pdfOverlay: Signal<IPdfOverlay> = this._processOverviewService.pdfOverlay;

  selectedState: Signal<ECalculusStateFilter> = computed(() => this._processOverviewService.selectedProcess().state);

  override GUID: string = AppControlGuids.TOutboxBook;
  override gridOptions: GridOptions = {
    ...SharedGridOptions,
    rowSelection: 'multiple',
    pagination: true,
  };
  override actions: ModelSignal<ITableAction[]> = model<ITableAction[]>([
    {
      displayName: 'Alle anordnen',
      controlAction: EControlActions.arrangeAll,
      disabledIfNoSelection: false,
      disabledIfMultiSelection: false,
      color: 'primary',
      icon: EsvgFiles.mail,
      tooltip: 'Alle Dokumente anordnen.',
      tooltipDelay: 1000,
      action: () => this.arrangeAll(),
      isHidden: (): boolean => {
        return this.selectedState() === ECalculusStateFilter.arrangeInvoiceOk;
      }
    },
    {
      displayName: 'DTA erneut anordnen',
      controlAction: EControlActions.arrange,
      disabledIfNoSelection: true,
      disabledIfMultiSelection: false,
      color: 'primary',
      icon: EsvgFiles.mail,
      tooltip: "Erstelle und versende die DTA-Datei des ausgewählten Dokuments erneut.",
      tooltipDelay: 1000,
      action: () => this.rearrangeDta(),
      isHidden: () => {
        return !(this.selectedState() === ECalculusStateFilter.arrangeInvoiceOk)
      }
    },
    {
      displayName: 'Dokument erneut versenden',
      controlAction: EControlActions.export,
      disabledIfNoSelection: true,
      disabledIfMultiSelection: true,
      color: 'primary',
      icon: EsvgFiles.mail,
      tooltip: 'Versende das ausgewählte Dokument erneut.',
      tooltipDelay: 1000,
      action: () => this.sendSelection(),
      isHidden: () => {
        return !(this.selectedState() === ECalculusStateFilter.arrangeInvoiceOk);
      }
    },
    {
      displayName: 'Rechnung anzeigen',
      controlAction: EControlActions.invoicePreview,
      disabledIfNoSelection: true,
      disabledIfMultiSelection: true,
      color: 'primary',
      icon: EsvgFiles.search,
      tooltip: 'Blende das Rechnungsdokument ein.',
      tooltipDelay: 1000,
      action: () => of(null).pipe(tap(() => this.pdfInvoiceToggle$.next(true))),
      isDisabled: () => {
        return this.isPdfOverlayLoading$.getValue();
      }
    },
    {
      displayName: 'Rechnung löschen',
      controlAction: EControlActions.deleteInvoice,
      disabledIfNoSelection: true,
      disabledIfMultiSelection: false,
      color: 'warn',
      icon: EsvgFiles.delete,
      tooltip: 'Lösche das ausgewählte Rechnungsdokument.',
      tooltipDelay: 1000,
      action: () => this.deleteInvoice(),
      isHidden: () => {
        return !(this.selectedState() === ECalculusStateFilter.arrangeInvoiceWarning);
      }
    },
    CSV_EXPORT_ACTION,
  ]);

  paginationSelectedRow$: Observable<IControlObject> = merge(
    this.paginationChanged$.pipe(
      map((paginationChange): IControlObject => {
        // only if the page size is one, the first row should be selected
        if (this.gridOptions.paginationPageSize === 1) {
          const rowIndex = paginationChange.api.paginationGetCurrentPage();
          paginationChange.api.setFocusedCell(rowIndex, "0");
          const firstRow = paginationChange.api.getDisplayedRowAtIndex(rowIndex);
          if (!firstRow?.data?.apiRow?.id) {
            return null;
          }
          this._rowSelection.setSelection(...[firstRow.data.apiRow.id]);
          return firstRow?.data?.apiRow.obj;
        }
        return null;
      }),
      filter((row: IControlObject) => row != null)),
    this.selectedDocumentOutbox$,
  ).pipe(
    // check if row has changed
    distinctUntilChanged((previous: IControlObject, current: IControlObject): boolean =>
      previous?.signature === current?.signature
    ),
    startWith(null),
  )

  pdfInvoiceToggle$: EventEmitter<boolean> = new EventEmitter<boolean>();
  isPdfOverlayLoading$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  isPdfShown$: Observable<boolean> = this.pdfInvoiceToggle$.pipe(
    scan((oldOverlay: boolean, newOverlay: boolean): boolean => {
      if (newOverlay === oldOverlay) {
        this.onHideOverlay();
        return false;
      }
      this.onShowOverlay();
      return true;
    }, null),
    startWith(false));

  pdfBlob$: Subscription = combineLatest([
      this.isPdfShown$,
      this.paginationSelectedRow$
    ]
  ).pipe(
    tap(() => this.isPdfOverlayLoading$.next(true)),
    switchMap(([showOverlay, invoiceControlObj]) => {
      if (invoiceControlObj == null || !showOverlay) {
        return of(null);
      }
      // If no current overlay is shown and the selected pagination row has changed select it.
      return this._outboxBookService.outboxBookDownloadCurrentInvoice({
        document: invoiceControlObj
      }).pipe(
        catchError((e) => {
          console.error(e);
          return of(e);
        }),
        map(v => ({blob: v}))
      ) as Observable<IPdfBlob>
    }),
    tap((res: IPdfBlob) => {
        this._processOverviewService.pdfOverlay$.next({type: 'documentOutBox', data: res})
        this.gridApi?.hideOverlay();
        this.isPdfOverlayLoading$.next(false);
      }
    ),
    catchError((e) => {
        this.gridApi?.hideOverlay();
        this.isPdfOverlayLoading$.next(false);
        return of(e);
      }
    )).pipe(
    takeUntilDestroyed()
  ).subscribe();

  constructor() {
    super();

    effect(() => {
      // restore selection after refresh
      const selectedDocumentOutbox: IControlObject = this.selectedDocumentOutbox();
      this.savedSelectedRowsIds = selectedDocumentOutbox ? [selectedDocumentOutbox?.signature] : [];
    });
    effect(() => {
      const pdf: IPdfOverlay = this.pdfOverlay();
      if (pdf?.type === 'documentOutBox') {
        this.pdf.set(pdf.data);
      } else {
        this.pdf.set(null);
      }
    }, {allowSignalWrites: true});

    this.subs.add(
      this._controlStore.controlDataChanged$.pipe(
        filter((evt: IControlDataChanged) => {
          return new Map([
            [AppControlGuids.TOutboxBook,
              new Set([
                EControlActions.arrange,
                EControlActions.arrangeAll,
                EControlActions.export,
                EControlActions.deleteInvoice,
              ])
            ],
          ]).get(evt.GUID)?.has(evt.changeType);
        }),
        tap(() => {
          this.refreshInfiniteCache()
        })).subscribe(),
    );

    this.subs.add(
      combineLatest([
        this._toolbarStore.toolbarFilterDto$,
        this.selectedProcess$.pipe(
          tap(() => {
            this.gridApi?.deselectAll();
          })
        ),
        this.selectedInvoice$
      ]).pipe(
        debounceTime(50),
        tap(([toolbarFilter, selectedProcess, selectedInvoice]) => {
          const {state, category} = selectedProcess;
          // Update the apiInstance and trigger the fetching of the data with the new filter.
          this.patchFilter({
            ...toolbarFilter,
            valuelist: typeof (state) === 'number' ? [state] : category ? [category] : undefined,
          });
        }),
      ).subscribe()
    );

    this.rowSelected.pipe(
      takeUntilDestroyed()
    ).subscribe((rows) => {
      if (!rows[0]?.data?.apiRow) {
        return;
      }
      this._processOverviewService.selectedDocumentOutbox$.next(rows[0].data.apiRow.obj)
    })
  }

  private arrangeAll(): Observable<any> {
    return this._outboxBookService.outboxBookArrangeAllInvoices({
      InstanceId: this.instanceId()
    }).pipe(
      tap((data: Blob) => {
        // Create download URL
        const downloadURL: string = URL.createObjectURL(data);

        const link: HTMLAnchorElement = document.createElement('a');
        link.href = downloadURL;
        link.download = 'invoices';
        link.click();
      }),
      finalize(() => this.gridApi?.hideOverlay())
    )
  }

  private sendSelection(): Observable<any> {
    const dialogData: IDispatchTypeSelectorDialogData = {
      title: 'Möchten Sie das Dokument per E-Post oder per E-Mail versenden oder herunterladen, um es manuell zu ' +
        'versenden?',
      entities: [
        {displayName: 'E-Post', id: EJobType.epost},
        {displayName: 'Manueller Versand', id: EJobType.manual},
        {displayName: 'E-Mail', id: EJobType.email},
      ],
      address: {
        addressline1: null,
        addressline2: null,
        addressline3: null,
        street: null,
        postcode: null,
        city: null,
        country: null,
      },
    };

    return this._outboxBookService.outboxBookInvoiceAddressData({document: this.selectedDocumentOutbox()}).pipe(
      tap((data: any): void => {
        dialogData.address = {
          addressline1: data.addressline1,
          addressline2: data.addressline2,
          addressline3: data.addressline3,
          street: data.street,
          postcode: data.postcode,
          city: data.city,
          country: data.country,
        };
      }),
      switchMap((): Observable<boolean> => {
        return this._dialogService.dialog.open(
          DispatchTypeSelectorComponent, {
            data: dialogData,
            maxWidth: '30rem',
          }
        ).afterClosed().pipe(
          concatMap((result: IDispatchTypeSelectorDialogReturnData): Observable<boolean> => {
            console.log(result);
            if (!result) {
              return of(null);
            }
            return this._outboxBookService.outboxBookReArrangeDocument({
              document: this.gridApi.getSelectedRows()[0].apiRow.obj,
              jobtype: result.selectedEntry as EJobType,
              changeaddress: result.changeAddress,
              addressdata: result.address,
            });
          }),
          finalize((): void => this.gridApi?.hideOverlay())
        )
      })
    )
  }

  private rearrangeDta(): Observable<any> {
    return forkJoin(
      this.gridApi.getSelectedRows().map(r => {
        return this._outboxBookService.outboxBookReArrangeDta({
          document: r.apiRow.obj
        })
      }))
      .pipe(
        finalize(() => this.gridApi?.hideOverlay())
      );
  }

  private onHideOverlay(): void {
    this.replaceDisplayNameOfAction(EControlActions.invoicePreview, 'Rechnung anzeigen')
  }

  private onShowOverlay(): void {
    this.replaceDisplayNameOfAction(EControlActions.invoicePreview, 'Rechnung ausblenden')
  }

  replaceDisplayNameOfAction(action: EControlActions, displayName: string): void {
    this.actions.update(actions => actions.map((tableAction: ITableAction): ITableAction => {
      if (tableAction.controlAction !== action) {
        return tableAction;
      } else {
        return {
          ...tableAction,
          displayName,
        };
      }
    }));
  }

  private deleteInvoice(): Observable<boolean> {
    return this.deleteLine(this._outboxBookService.outboxBookDeleteInvoice({
      documents: this.gridApi.getSelectedRows().map(row => row.apiRow.obj),
      InstanceId: this.instanceId()
    }));
  }
}
